// client/src/scheduling/hooks/useTaskBudgetCache.js
// Per-task budgets for the IDD tab's jobs, fetched in batches and cached per PEJobIndex.
//
//   const { taskCache, ensureTasks, resetTaskCache } = useTaskBudgetCache(fetchJobTaskBudgetsBatch);
//   useEffect(() => { ensureTasks(ids, cycleName, taskCache); }, [ids, cycleName, taskCache, ensureTasks]);
//   // on a full reload: resetTaskCache()  (synchronously, next to setJobs)
//
// ensureTasks fetches only ids that are neither cached nor in flight, <= batchSize per
// call, and every id it asked for ends up cached ([] when the server had no rows or the
// call failed), so the effect above settles after one round.
//
// THE LOOP THIS EXISTS TO PREVENT (2026-10-19, found on first run in a browser): the first
// version decided "is this result still wanted?" INSIDE the setTaskCache updater by
// checking the in-flight set, and cleared that set right after calling setState. React
// only runs an updater immediately when the component has nothing else queued; on a busy
// page like IDDReview it runs during the next render, so it saw an empty set, kept
// nothing, and still returned a new object. The new taskCache re-ran the effect, the ids
// were still missing, and it fetched them again, forever. So: decide when the response
// arrives (with a reset generation), and make the updater a plain merge.
import { useState, useRef, useCallback, useEffect } from 'react';

export const TASK_BATCH_SIZE = 500;   // the server caps a call at 1000 ids

export function useTaskBudgetCache(fetchBatch, batchSize = TASK_BATCH_SIZE) {
  const [taskCache, setTaskCache] = useState({});
  const inFlight = useRef(new Set());
  const generation = useRef(0);
  // Held in a ref so ensureTasks stays stable even if a caller passes an inline function
  // (a new ensureTasks every render would re-run the caller's effect every render).
  const fetchRef = useRef(fetchBatch);
  useEffect(() => { fetchRef.current = fetchBatch; }, [fetchBatch]);

  const ensureTasks = useCallback((ids, cycleName, cache) => {
    const flying = inFlight.current;     // the set THIS fetch belongs to (a reset replaces it)
    const need = [...new Set(ids)].filter(pe => !(pe in cache) && !flying.has(pe));
    if (need.length === 0) return;
    const gen = generation.current;
    need.forEach(pe => flying.add(pe));
    for (let i = 0; i < need.length; i += batchSize) {
      const chunk = need.slice(i, i + batchSize);
      Promise.resolve()
        .then(() => fetchRef.current(chunk, cycleName))
        .catch(() => [])
        .then(rows => {
          chunk.forEach(pe => flying.delete(pe));
          if (gen !== generation.current) return;   // reset since this started: stale rows
          const byPe = {};
          for (const pe of chunk) byPe[pe] = [];
          for (const r of (Array.isArray(rows) ? rows : [])) {
            if (byPe[r.PEJobIndex]) byPe[r.PEJobIndex].push(r);
          }
          setTaskCache(prev => ({ ...prev, ...byPe }));
        });
    }
  }, [batchSize]);

  const resetTaskCache = useCallback(() => {
    generation.current += 1;
    inFlight.current = new Set();
    setTaskCache({});
  }, []);

  return { taskCache, ensureTasks, resetTaskCache };
}
